import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChatMessage as ChatMessageType, useChatService } from '../../services/chatService'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import { supabase } from '../../services/supabase'

interface WhatsAppChatWidgetProps {
  className?: string
  userId?: string
  isFullscreen?: boolean
}

export const WhatsAppChatWidget: React.FC<WhatsAppChatWidgetProps> = ({
  className = '',
  userId,
  isFullscreen = false
}) => {
  const [messages, setMessages] = useState<ChatMessageType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [whatsappStatus, setWhatsappStatus] = useState({
    registered: false,
    verified: false,
    phoneNumber: undefined as string | undefined,
    referralCode: undefined as string | undefined
  })
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const {
    connect,
    sendMessage,
    getMessageHistory,
    onMessage,
    offMessage,
    checkWhatsAppStatus,
    generateWhatsAppLink
  } = useChatService()

  // Get current user ID if not provided
  const [currentUserId, setCurrentUserId] = useState<string | null>(userId || null)

  useEffect(() => {
    if (!userId) {
      const getCurrentUser = async () => {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          setCurrentUserId(user.id)
        }
      }
      getCurrentUser()
    }
  }, [userId])

  // Initialize chat service
  useEffect(() => {
    if (!currentUserId) return

    let cleanup: (() => void) | undefined

    const initializeChat = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Check WhatsApp status first (with timeout)
        const statusPromise = checkWhatsAppStatus(currentUserId)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Status check timeout')), 5000)
        )

        const status = await Promise.race([statusPromise, timeoutPromise]) as any
        setWhatsappStatus({
          registered: status?.registered || false,
          verified: status?.verified || false,
          phoneNumber: status?.phoneNumber || undefined,
          referralCode: status?.referralCode || undefined
        })

        // Connect to chat service (with timeout)
        const connectPromise = connect(currentUserId)
        const connectTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Connection timeout')), 10000)
        )

        await Promise.race([connectPromise, connectTimeoutPromise])
        setIsConnected(true)

        // Load message history (with timeout and fallback)
        try {
          const historyPromise = getMessageHistory(currentUserId)
          const historyTimeoutPromise = new Promise<ChatMessageType[]>((resolve) => 
            setTimeout(() => resolve([]), 5000)
          )
          
          const history = await Promise.race([historyPromise, historyTimeoutPromise])
          setMessages(history || [])
        } catch (historyError) {
          console.warn('Failed to load message history:', historyError)
          setMessages([]) // Continue with empty history
        }

        // Set up message listener
        const handleNewMessage = (message: ChatMessageType) => {
          setMessages(prev => [...prev, message])
        }
        onMessage(handleNewMessage)

        cleanup = () => {
          offMessage(handleNewMessage)
        }
      } catch (error) {
        console.error('Failed to initialize chat:', error)
        setError('Chat service unavailable. You can still send messages via WhatsApp.')
        // Set some basic functionality even on error
        setIsConnected(false)
        setWhatsappStatus({
          registered: false,
          verified: false,
          phoneNumber: undefined,
          referralCode: undefined
        })
      } finally {
        setIsLoading(false)
      }
    }

    initializeChat()

    return () => {
      if (cleanup) cleanup()
    }
  }, [currentUserId])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = async (message: string) => {
    if (!currentUserId || !isConnected) return

    try {
      const response = await sendMessage(message, currentUserId)
      if (response.success) {
        // Message will be added to UI via real-time subscription
        // or we can add it immediately for better UX
        const newMessage: ChatMessageType = {
          id: `temp-${Date.now()}`,
          conversation_id: response.conversationId || '',
          user_id: currentUserId,
          phone_number: whatsappStatus.phoneNumber || '',
          message_type: 'text',
          content: message,
          direction: 'outbound',
          status: 'sent',
          created_at: new Date().toISOString()
        }
        setMessages(prev => [...prev, newMessage])
      } else {
        setError(response.error || 'Failed to send message')
        setTimeout(() => setError(null), 5000)
      }
    } catch (error) {
      console.error('Send message error:', error)
      setError('Failed to send message')
      setTimeout(() => setError(null), 5000)
    }
  }

  const handleWhatsAppRegistration = () => {
    const registrationMessage = 'Hi! I want to register for TyreLink and get my referral code.'
    const whatsappUrl = generateWhatsAppLink(registrationMessage)
    window.open(whatsappUrl, '_blank')
  }

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  }

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-lg ${className}`}>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="text-sm text-gray-500">Connecting to WhatsApp...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={`
        bg-white rounded-lg shadow-lg overflow-hidden
        ${isFullscreen ? 'h-screen' : 'h-96 max-h-screen'}
        ${className}
      `}
    >
      {/* Chat header */}
      <div className="bg-green-500 text-white p-4 flex items-center space-x-3">
        <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold">TyreLink Support</h3>
          <p className="text-sm opacity-90">
            {isConnected ? 'Online • Typically replies instantly' : 'Connecting...'}
          </p>
        </div>
        {whatsappStatus.registered && whatsappStatus.referralCode && (
          <div className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
            Code: {whatsappStatus.referralCode}
          </div>
        )}
      </div>

      {/* WhatsApp registration prompt */}
      {!whatsappStatus.registered && (
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 mb-1">Register with WhatsApp</h4>
              <p className="text-sm text-blue-700 mb-3">
                Get instant support, tyre recommendations, and earn rewards through our WhatsApp bot.
              </p>
              <button
                onClick={handleWhatsAppRegistration}
                className="inline-flex items-center px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                </svg>
                Register on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-50 border-b border-red-200 p-3"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to TyreLink Support</h3>
            <p className="text-sm text-gray-500 max-w-xs">
              {whatsappStatus.registered 
                ? "Ask me anything about tyres, bookings, or our services!"
                : "Register with WhatsApp above to start chatting with our AI assistant."
              }
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                isOwn={message.direction === 'outbound'}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Chat input */}
      {whatsappStatus.registered ? (
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={!isConnected}
          placeholder={isConnected ? "Type your message..." : "Connecting..."}
        />
      ) : (
        <div className="p-4 bg-gray-100 border-t">
          <div className="text-center text-sm text-gray-600">
            Register with WhatsApp above to start chatting
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default WhatsAppChatWidget